import { useCallback, useState } from 'react';

import { Event } from '@mui/icons-material';
import { ActionItemKeys, NotificationResponse } from '@uplift/api';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { trackDismissToDo, useAllActionItems, useL10n } from '@uplift/hooks';
import { CalendarDateService, formatTime } from '@uplift/services';
import { UserRole } from '@uplift/types';
import { BaseDashboardActionItem } from '@uplift/ui-v2';

import { CheckInModal } from './check-in.modal';

export type NewPsychAppointmentProps = {
  clientId: string;
  clientName: string;
  nextPsychiatryAppointment: string;
  nextTherapyAppointment: string;
};

export const NewPsychAppointment: React.VFC<NewPsychAppointmentProps> = ({
  clientId, clientName, nextPsychiatryAppointment, nextTherapyAppointment
}) => {
  const l10n = useL10n();
  const me = useCurrentuser();
  const [ showModal, setShowModal ] = useState(false);
  const timezone = useTimezone();
  const { dismiss } = useAllActionItems();

  const psychAppt = CalendarDateService.date(nextPsychiatryAppointment).tz(timezone);
  const psychDate = psychAppt.format('MMM Do');
  const psychTime = formatTime(psychAppt);

  const therapyAppt = CalendarDateService.date(nextTherapyAppointment).tz(timezone);
  const therapyDate = therapyAppt.format('MMM Do');
  const therapyTime = formatTime(therapyAppt);

  const handleClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const handleDismiss = useCallback(() => {
    trackDismissToDo({
      toDoType: ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH,
      userType: UserRole.provider,
      reason: NotificationResponse.dismiss
    });
    dismiss({
      key: ActionItemKeys.PROVIDER_PATIENT_SCHEDULED_PSYCH,
      id: clientId,
    });
  }, [ clientId, dismiss ]);

  const handleMessage = useCallback(() => {
    setShowModal(true);
  }, []);

  return (
    <>
      <BaseDashboardActionItem
        icon={<Event />}
        color="orange"
        title={l10n.dashboard.actionItems.clientScheduledPsych.title(clientName, psychDate, psychTime)}
        details={l10n.dashboard.actionItems.clientScheduledPsych.subtitle(therapyDate, therapyTime)}
        body={l10n.dashboard.actionItems.clientScheduledPsych.body(clientName)}
        action={{
          text: l10n.dashboard.actionItems.clientScheduledPsych.actionText,
          handleClick: handleMessage
        }}
        dismiss={{
          text: l10n.dashboard.actionItems.clientScheduledPsych.dismissText,
          handleClick: handleDismiss
        }}
      />
      <CheckInModal
        open={showModal}
        clientName={clientName}
        handleClose={handleClose}
        patientId={clientId}
        providerId={me.id}
      />
    </>
  );
};
